import type { UserDto, UserProfileDto } from "@gatiq/shared";

export interface ProfileKpi { key: string; label: string; value: number; hint: string }
type ProfileMetrics = { user: Pick<UserDto, "role">; counts: UserProfileDto["counts"]; summary: UserProfileDto["summary"] };
const num = (value: number | null) => (value ?? 0).toLocaleString("en-IN");

/** Role responsibilities determine the strip, independently of viewer permissions. */
export function profileKpis({ user, counts, summary: s }: ProfileMetrics): ProfileKpi[] {
  const rows: ProfileKpi[] = [];
  const add = (key: string, label: string, value: number | null, hint: string) => { if (value !== null) rows.push({ key, label, value, hint }); };
  if (user.role === "karigar") {
    add("balance", "Available QCoin", s.balance, "Personal reward balance");
    add("earned", "QCoin earned", s.earned, "Total personal earnings");
    add("scans", "Product scans", s.scans, "Codes scanned by this Karigar");
    add("redeemed", "QCoin redeemed", s.settledPoints, `${num(s.settledCount)} completed redemptions`);
    add("pending", "Pending QCoin", s.pendingPoints, "Requested and awaiting payout");
  } else if (user.role === "counter") {
    add("balance", "Counter QCoin", s.balance, `${num(s.earned)} QCoin earned`);
    add("karigars", "Linked Karigars", counts.karigars, `${num(counts.activeKarigars)} active and approved`);
    add("approvals", "Pending KYC", counts.pendingKyc, "Linked Karigars awaiting approval");
    add("payouts", "Payouts handled", s.settledCount, `${num(s.settledPoints)} QCoin paid by this counter`);
    add("reimbursement", "Reimbursement due", s.outstandingReimbursement, "QCoin awaiting reimbursement");
    add("stock", "Stock on hand", s.stock, `${num(s.received)} units received`);
  } else if (user.role === "sales_rep") {
    add("counters", "Counters covered", counts.counters, `${num(counts.activeCounters)} active counters`);
    add("karigars", "Karigar coverage", counts.karigars, `${num(counts.activeKarigars)} active and approved`);
    add("team", "Team managed", counts.staff, `${num(counts.direct)} direct network links`);
    add("approvals", "Pending KYC", counts.pendingKyc, "Across the assigned network");
    add("scans", "Network scans", s.scans, `${num(s.activeScanners)} Karigars have scanned`);
  } else if (["distributor", "sub_distributor"].includes(user.role)) {
    add("stock", "Stock on hand", s.stock, `${num(s.received)} units received`);
    add("supplied", "Units supplied", s.sent, "Completed outbound stock movements");
    add("counters", "Linked counters", counts.counters, `${num(counts.activeCounters)} active counters`);
    add("team", "Downstream team", counts.staff, `${num(counts.karigars)} Karigars in the network`);
    add("scans", "Network scans", s.scans, `${num(s.activeScanners)} Karigars have scanned`);
  }
  return rows;
}
